import { symRoundedHome } from '@quasar/extras/material-symbols-rounded'
import { customMenuItems, type CustomMenuItem } from 'src/CrudManager/config/customMenuItems'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import { useAppSettingsStore } from 'src/CrudManager/Core/stores/useAppSettingsStore'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { BOTTOM_NAV_HOME_ID, useBottomNavStore } from 'src/stores/useBottomNavStore'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export { BOTTOM_NAV_HOME_ID }

export interface BottomNavSourceItem {
  id: string
  label: string
  icon: string | undefined
  category: string
  kind: 'route' | 'download'
  to?: string
  downloadUrl?: string
  filename?: string
}

const customItemPasses = (item: CustomMenuItem, userStore: ReturnType<typeof useUserStore>, appSettings: ReturnType<typeof useAppSettingsStore>): boolean => {
  if (item.companyIds?.length) {
    const currentId = appSettings.currentCompany?.id
    if (!currentId || !item.companyIds.includes(currentId)) return false
  }
  if (item.roles?.length) {
    return item.roles.some((role) => userStore.currentUserHasRole(role))
  }
  if (item.permissions?.length) {
    return item.permissions.some((permission) => userStore.hasPermission(permission))
  }
  return true
}

export function useBottomNavSource() {
  const store = useBottomNavStore()
  const crudManagerStore = useCrudManagerStore()
  const userStore = useUserStore()
  const appSettings = useAppSettingsStore()
  const { t } = useI18n()
  const { canUserCrud, generateModuleUrl, getModelMeta } = useCrudUtils()

  const crudItems = computed<BottomNavSourceItem[]>(() => {
    if (!crudManagerStore.isInitiated) return []
    return crudManagerStore.crudInstances
      // external recents stamps are not navigable model roots
      .filter((i) => !i.parentId && i.moduleName !== 'external')
      .filter((i) => {
        const meta = getModelMeta(i.modelName)
        if (meta?.disabled) return false
        return canUserCrud(i.modelName, 'read') && canUserCrud(i.modelName, 'list')
      })
      .map<BottomNavSourceItem>((i) => {
        const meta = getModelMeta(i.modelName)
        const url = generateModuleUrl(i.modelName, i.moduleName, i.props?.modelId as string) || ''
        return {
          id: `crud:${i.modelName}`,
          label: t(`crudmodels.${i.modelName}.name`, 2),
          icon: meta?.icon,
          category: meta?.category || 'operations',
          kind: 'route',
          to: url,
        }
      })
  })

  const flattenLeaves = (item: CustomMenuItem, parentCategory?: string): CustomMenuItem[] => {
    if (item.type === 'menu') {
      return (item.childs ?? []).flatMap((child) => flattenLeaves(child, item.category))
    }
    return [{ ...item, category: parentCategory ?? item.category }]
  }

  const customItems = computed<BottomNavSourceItem[]>(() =>
    customMenuItems
      .flatMap((item) => flattenLeaves(item))
      .filter((item) => customItemPasses(item, userStore, appSettings))
      .map<BottomNavSourceItem>((item) => ({
        id: item.id,
        label: t(`crudmanager.customMenu.${item.label}`),
        icon: item.icon,
        category: item.category,
        kind: item.type === 'download' ? 'download' : 'route',
        to: item.to,
        downloadUrl: item.downloadUrl,
        filename: item.filename,
      })),
  )

  const homeItem = computed<BottomNavSourceItem>(() => ({
    id: BOTTOM_NAV_HOME_ID,
    label: t('crudmanager.bottomNav.home'),
    icon: symRoundedHome,
    category: 'system',
    kind: 'route',
    to: '/',
  }))

  const allItems = computed<BottomNavSourceItem[]>(() => {
    const map = new Map<string, BottomNavSourceItem>()
    // Home is always first and always present, independent of CRUD config.
    map.set(homeItem.value.id, homeItem.value)
    for (const item of [...crudItems.value, ...customItems.value]) {
      if (!map.has(item.id)) map.set(item.id, item)
    }
    return Array.from(map.values())
  })

  const knownIds = computed<Set<string>>(() => {
    const ids = new Set<string>()
    ids.add(BOTTOM_NAV_HOME_ID)
    if (crudManagerStore.isInitiated) {
      for (const inst of crudManagerStore.crudInstances) {
        if (!inst.parentId) ids.add(`crud:${inst.modelName}`)
      }
    }
    const collectIds = (item: CustomMenuItem) => {
      ids.add(item.id)
      if (item.type === 'menu' && item.childs) item.childs.forEach(collectIds)
    }
    customMenuItems.forEach(collectIds)
    return ids
  })

  const DEFAULT_ORDER: string[] = [
    BOTTOM_NAV_HOME_ID,
    'crud:service_call',
    'crud:transaction',
    'crud:business_partner',
    'crud:part',
  ]

  const defaultItemIds = computed<string[]>(() => {
    const available = new Set(allItems.value.map((i) => i.id))
    return DEFAULT_ORDER.filter((id) => available.has(id))
  })

  const effectiveIds = computed<string[]>(() => {
    const base = store.hasUserCustomized ? store.enabledItemIds : defaultItemIds.value
    // Pin Home: always first, never removable, regardless of user customisation.
    const withoutHome = base.filter((id) => id !== BOTTOM_NAV_HOME_ID)
    return [BOTTOM_NAV_HOME_ID, ...withoutHome]
  })

  const resolvedItems = computed<BottomNavSourceItem[]>(() => {
    const byId = new Map(allItems.value.map((i) => [i.id, i]))
    const out: BottomNavSourceItem[] = []
    for (const id of effectiveIds.value) {
      const item = byId.get(id)
      if (item) out.push(item)
    }
    return out
  })

  const moreSlotNeeded = computed<boolean>(() => {
    const enabled = resolvedItems.value.length
    const total = allItems.value.length
    return enabled > store.maxItems || total > enabled
  })

  const visibleSlots = computed<BottomNavSourceItem[]>(() => {
    const items = resolvedItems.value
    if (!moreSlotNeeded.value) return items
    return items.slice(0, store.maxItems - 1)
  })

  const overflowItems = computed<BottomNavSourceItem[]>(() => {
    if (!moreSlotNeeded.value) return []
    const visibleIds = new Set(visibleSlots.value.map((i) => i.id))
    const enabledOverflow = resolvedItems.value.filter((i) => !visibleIds.has(i.id))
    const enabledIds = new Set(resolvedItems.value.map((i) => i.id))
    const notEnabled = allItems.value.filter((i) => !enabledIds.has(i.id))
    return [...enabledOverflow, ...notEnabled]
  })

  watch(
    [knownIds, () => crudManagerStore.isInitiated],
    ([ids, initiated]) => {
      if (!initiated) return
      if (!store.hasUserCustomized) return
      store.pruneMissing(ids)
    },
    { immediate: true, flush: 'post' },
  )

  return {
    allItems,
    defaultItemIds,
    resolvedItems,
    visibleSlots,
    overflowItems,
  }
}
